import React from "react"
import { QueryClient, useQuery } from "@tanstack/react-query"
import { performRequest } from "../../../../common/utils"

const queryClient = new QueryClient()

const isValidEmail = ( email ) => {
  const regex = /^[^\s@]+[+]*[^\s@]*@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
}

const isEmailExistInArray = ( members, email, index ) => {
  return members.some(
    (member, key) =>
      member.email.value === email 
      && key !== index 
  )
}

const addMemberModalId = ( teamId = 0 ) => {
  return `msync-woo-membership-team-add-member-modal-${teamId}`
}

const inputError = ( value ) => {
  return (
    <>
    {
      value && 
      <div className="msync-input-error">
        <small>{ value }</small>
      </div>
    }
    </>  
  )
}

/* ----- Password Validation -------- */

const validatePassword = ( password ) => {

  // Minimum length requirement
  const minLength = 8

  // Presence of both uppercase and lowercase letters
  const upperCaseRegex = /[A-Z]/
  const lowerCaseRegex = /[a-z]/
  
  // Inclusion of numbers and special characters
  const numberRegex = /[0-9]/
  const specialCharRegex = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/

  // Avoidance of common or easily guessable passwords (optional)
  const commonPasswords = ['password', '123456', 'qwerty']

  const isLengthValid = password.length >= minLength
  const hasUpperCase = upperCaseRegex.test(password)
  const hasLowerCase = lowerCaseRegex.test(password)
  const hasNumber = numberRegex.test(password)
  const hasSpecialChar = specialCharRegex.test(password)
  const isCommonPassword = commonPasswords.includes(password.toLowerCase())

  const strengthLevel =
    ( isLengthValid ? 1 : 0 ) +
    ( hasUpperCase ? 1 : 0 ) +
    ( hasLowerCase ? 1 : 0 ) +
    ( hasNumber ? 1 : 0 ) +
    ( hasSpecialChar ? 1 : 0 ) -
    ( isCommonPassword ? 1 : 0 )

  return strengthLevel

}

const getStrengthLabel = ( strengthLevel ) => {
  switch(true) {
    case strengthLevel > 4:
      return 'Very Strong'
    case strengthLevel > 3:
      return 'Strong'
    case strengthLevel > 2:
      return 'Medium'
    case strengthLevel > 1:
      return 'Weak'
    case strengthLevel > 0:
      return 'Very Weak'
    default:
      return ''
  }
}

const getStrengthColor = ( strength ) => {
  switch(strength) {
    case 'Very Weak':
    case 'Weak':
      return 'red'
    case 'Medium':
      return 'orange'
    case 'Strong':
    case 'Very Strong':
      return 'green'
    default:
      return 'black'
  }
}


const formRequest = ( formData = [], setMessageResponse = null, hasRefetch = null ) => {

  const customData = {}
  for (const pair of formData.entries()) {
    customData[pair[0]] = pair[1]
  }

  const {
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    data } = useQuery({
      queryKey: [formData.get('action')],
      queryFn: request,
      refetchOnWindowFocus: false,
      enabled: false
    }, queryClient)

    async function request(){
      try {
        // const perform = await performRequest(group_dashboard.ajaxurl, formData)
        // const res = await perform.json()
        const res = await Tangible?.ajax(customData.action, customData)
        if( ! res.success ){
          throw new Error( res.errors.shift() )
        }
        if( hasRefetch !== null ){
          hasRefetch()
        }
        return res.data
      } catch (error) {
        console.error( error )
        if( setMessageResponse !== null ){
          setMessageResponse( error.toString() )
        }
        return false
      }
    }

    return {
      isLoading,
      isFetching,
      isError,
      error,
      refetch,
      data
    }

}


const fetchCSVTeamData = ( teamId, teamType ) => {

  const {
    isLoading,
    isPending,
    isFetching,
    isError,
    error,
    data } = useQuery({
      queryKey: ['msync_get_csv_team_data'],
      queryFn: request,
      refetchOnWindowFocus: false,
    }, queryClient)

    async function request(){
      // const formData = new FormData()
      // formData.append('action', 'msync_get_csv_team_data')
      // formData.append('team_id', teamId)
      // formData.append('team_type', teamType)
      // formData.append('nonce', group_dashboard.nonce_members)
      try {
        // const perform = await performRequest(group_dashboard.ajaxurl, formData)
        // const res = await perform.json()
        const perform = await Tangible?.ajax('msync_get_csv_team_data', {
          team_id: teamId,
          team_type: teamType
        })
        const res = perform
        if( ! res.success ){
          throw new Error( res.errors.shift() )
        }
        return res.data
      } catch (error) {
        console.error( error )
        return false
      }
    }

    return { 
      isLoading,
      isPending,
      isFetching,
      isError,
      error,
      data }

}

const isIterable = ( obj ) => {
  return typeof obj?.[Symbol.iterator] === 'function';
}

const createField = ( error = false ) => ({
  value: '',
  error: error,
  message: ''
})

const membersArray = () => ({
  firstName: createField(),
  lastName: createField(),
  role: createField( true ),
  username: createField( true ),
  email: createField( true ),
  password: createField( true ),
  strength: '',
})

const errorMessage = {
  required: 'This is a required field',
  validEmail: 'Please enter a valid email address',
  existEmail: 'Email already exists in the form',
}

const isLink = ( value ) => {
  const urlPattern = /^(https?:\/\/[^\s$.?#].[^\s]*)$/i;
  return urlPattern.test(value);
}

export {
  addMemberModalId,
  isValidEmail,
  validatePassword,
  getStrengthLabel,
  getStrengthColor,
  formRequest,
  queryClient,
  isIterable,
  fetchCSVTeamData,
  inputError,
  isEmailExistInArray,
  membersArray,
  errorMessage,
  isLink
}
